import { I18n, i18n } from '@lingui/core';
import { I18nProvider, useLingui } from '@lingui/react';
import React, { ComponentType } from 'react';

const LOCALES = {
  en: 'English',
  id: 'Indonesian',
};
const DEFAULT_LOCALE = 'en';
const STORAGE_KEY = 'lang_53f7e069';

export type LocaleKey = keyof typeof LOCALES;

function validLocaleKeyOrNull(key: string | null): LocaleKey | null {
  if (key === null) return null;
  return Object.keys(LOCALES).includes(key) ? key as LocaleKey : null;
}

export async function dynamicActivate(locale: LocaleKey) {
  const { messages } = await import(`./locales/${locale}`);
  console.log(`Activating locale: ${locale}`, messages);
  i18n.load(locale, messages);
  i18n.activate(locale);
  sessionStorage.setItem(STORAGE_KEY, locale);
}


type withI18nProps = {
  i18n: I18n
}

// legacy impl for class components for useLingui react hook
// https://github.com/lingui/js-lingui/blob/31dcab5a9a8f88bfa8b3a2c7cd12aa2d908a1d80/packages/react/src/I18nProvider.tsx#L33
export function withI18n(
  o?: object
): <P extends withI18nProps>(
  // eslint-disable-next-line no-unused-vars
  Component: ComponentType<P>
) => React.ComponentType<P> {
  return <P extends withI18nProps>(
    WrappedComponent: ComponentType<P>
  ): ComponentType<P> => {
    return props => {
      if (process.env.NODE_ENV !== 'production') {
        if (typeof o === 'function' || React.isValidElement(o)) {
          throw new Error(
            'withI18n([options]) takes options as a first argument, ' +
              'but received React component itself. Without options, the Component ' +
              'should be wrapped as withI18n()(Component), not withI18n(Component).'
          );
        }
      }

      const { i18n } = useLingui();
      return <WrappedComponent {...props} i18n={i18n} />;
    };
  };
}

export const LangProvider = (props: React.PropsWithChildren<{}>) => {
  React.useEffect(() => {
    const localeToUse = validLocaleKeyOrNull(sessionStorage.getItem(STORAGE_KEY)) || DEFAULT_LOCALE;
    dynamicActivate(localeToUse);
  }, []);

  return <I18nProvider i18n={i18n}>
    {props.children}
  </I18nProvider>;
};


export { languageAPI } from './api';
